import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * API slice for form suggestion service
 * Handles auto-completion and AI-powered form assistance
 */
export const formSuggestionApiSlice = createApi({
  reducerPath: 'formSuggestion',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BACKEND_URL,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      // Get token from state
      const token = getState().auth?.token;
      
      // If token exists, add authorization header
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      
      return headers;
    }
  }),
  tagTypes: ['FormSuggestion'],
  endpoints: (builder) => ({
    // Get suggestions for a specific field as user types
    getFieldSuggestions: builder.query({
      query: ({ formType, fieldName, inputValue, contextData }) => ({
        url: '/form-suggestions/field',
        method: 'POST',
        body: { formType, fieldName, inputValue, contextData }
      }),
      // Transform response to handle different backend formats
      transformResponse: (response) => {
        if (Array.isArray(response)) {
          return response;
        } else if (response?.suggestions && Array.isArray(response.suggestions)) {
          return response.suggestions;
        }
        return [];
      },
      // Keep last 5 results for quick re-use
      keepUnusedDataFor: 300 // 5 minutes
    }),
    
    // Get AI-powered form suggestions for complex fields or sections
    formSuggestion: builder.mutation({
      query: ({ formType, step, fieldContext, query, mode = 'chat' }) => ({
        url: '/form-suggestions/ai',
        method: 'POST',
        body: { formType, step, fieldContext, query, mode }
      })
    }),
    
    // Report user selection for learning
    reportSelection: builder.mutation({
      query: ({ formType, fieldName, selectedValue, allSuggestions }) => ({
        url: '/form-suggestions/report-selection',
        method: 'POST',
        body: { formType, fieldName, selectedValue, allSuggestions }
      }),
      // Don't wait for this to complete - fire and forget
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          // Silently fail - this is just for analytics
          console.error('Failed to report selection', err);
        }
      }
    })
  })
});

// Export the generated hooks
export const {
  useGetFieldSuggestionsQuery,
  useFormSuggestionMutation,
  useReportSelectionMutation
} = formSuggestionApiSlice;
